import { validateActionRequirements } from './utils/validateActionRequirements'

const MAX_SUGGESTIONS = 5

export function createPathnameMatcher(options: {
  exact?: string | string[]
  startsWith?: string
  lastSegment?: {
    is?: string | string[]
    isNot?: string | string[]
  }
}): (pathname: string) => boolean {
  return (pathname: string) => {
    // Check exact match
    if (options.exact) {
      const exactValues = Array.isArray(options.exact) ? options.exact : [options.exact]
      if (!exactValues.includes(pathname)) {
        return false
      }
    }

    if (options.startsWith) {
      if (!pathname.startsWith(options.startsWith)) {
        return false
      }
    }

    if (options.lastSegment) {
      const segments = pathname.split('/').filter(Boolean)
      const lastSegment = segments[segments.length - 1]

      if (options.lastSegment.is) {
        const isValues = Array.isArray(options.lastSegment.is) ? options.lastSegment.is : [options.lastSegment.is]
        if (!isValues.includes(lastSegment)) {
          return false
        }
      }

      if (options.lastSegment.isNot) {
        const isNotValues = Array.isArray(options.lastSegment.isNot)
          ? options.lastSegment.isNot
          : [options.lastSegment.isNot]
        if (isNotValues.includes(lastSegment)) {
          return false
        }
      }
    }

    return true
  }
}

export interface SuggestionItem {
  text: string
  actionType?: string
  customValidator?: (context: SuggestionContext) => boolean
  priority?: number
}

export interface SuggestionContext {
  pathname: string
  isStudioLoaded: boolean
  hasSelectedSystem: boolean
  selectedSystem?: any
}

export interface MessageSuggestionConfig {
  suggestions: (string | SuggestionItem)[]
  requiresDesignTab?: boolean
  requiresStudioLoaded?: boolean
  requiresSelectedSystem?: boolean
  requiresPanels?: boolean
  requiresBattery?: boolean
  pathnameMatcher?: (pathname: string) => boolean
  customValidator?: (context: SuggestionContext) => boolean
  priority?: number
}

export const MESSAGE_SUGGESTIONS: Record<string, MessageSuggestionConfig> = {
  systemTemplates: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/system_templates',
    }),
    suggestions: ['How do Auto Design Templates work?', 'Help me create an Auto Design Template'],
  },
  designTabWithSystem: {
    requiresDesignTab: true,
    requiresSelectedSystem: true,
    suggestions: ['What are the specifications of this system?'],
  },
  designTabWithPanels: {
    requiresDesignTab: true,
    requiresSelectedSystem: true,
    requiresStudioLoaded: true,
    requiresPanels: true,
    suggestions: ['How do I save this system as a template?', 'How do I run auto-design?'],
  },
  designTabBatterySettings: {
    requiresDesignTab: true,
    requiresSelectedSystem: true,
    requiresStudioLoaded: true,
    requiresBattery: true,
    priority: 11,
    suggestions: [
      'How do I configure battery control schemes?',
      'What battery control schemes are available?',
      'How do battery dispatch rules work?',
      'How do I set up peak demand shaving?',
      'How do I configure battery charging rules?',
    ],
  },
  projectConfigurationsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/project_configurations',
    }),
    suggestions: [
      'What are design settings?',
      'How do setbacks affect panel placement?',
      'What is the difference between setback types?',
    ],
  },
  designTabActionSuggestions: {
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    requiresSelectedSystem: true,
    priority: 10,
    suggestions: [
      {
        text: 'String my inverter for me',
        actionType: 'runAutoString',
        priority: 12,
      },
      {
        text: 'Suggest me a battery',
        actionType: 'runBatteryDesignAssistant',
        priority: 14,
        customValidator: (context) => {
          // Don't show if there are already batteries in the system
          if (!context.hasSelectedSystem || !context.selectedSystem) {
            return false
          }
          const batteryCount = context.selectedSystem.batteries().length
          return batteryCount === 0
        },
      },
      {
        text: 'Suggest me an inverter',
        actionType: 'runInverterDesignAssistant',
        priority: 13,
        customValidator: (context) => {
          // Don't show if there are already inverters in the system
          if (!context.hasSelectedSystem || !context.selectedSystem) {
            return false
          }
          const inverterCount = context.selectedSystem.inverters().length
          return inverterCount === 0
        },
      },
      {
        text: 'Save this system as an Auto Design Template',
        actionType: 'saveSystemDesignAsTemplate',
        priority: 15,
      },
    ],
  },
  designTabActionSuggestionsWithPanels: {
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    requiresSelectedSystem: true,
    requiresPanels: true,
    priority: 10,
    suggestions: [
      {
        text: 'Save this system as an Auto Design Template',
        actionType: 'saveSystemDesignAsTemplate',
        priority: 15,
      },
    ],
  },
  designTabActionSuggestionsNoSystem: {
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    priority: 10,
    suggestions: [
      {
        text: 'Run Auto Design from my latest Auto Design Template',
        actionType: 'autoDesignEphemeral',
        priority: 15,
      },
    ],
  },
  homePage: {
    pathnameMatcher: createPathnameMatcher({
      exact: ['/home', '/'],
    }),
    suggestions: ['How do I create a new project?', 'What can I do with OpenSolar?', 'Show me my recent projects'],
  },
  projectsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/projects',
      lastSegment: {
        isNot: ['manage', 'energy', 'design', 'payments', 'proposal', 'preview', 'hardware', 'sld'],
      },
    }),
    suggestions: ['How do I create a new project?'],
  },
  controlDashboard: {
    pathnameMatcher: createPathnameMatcher({
      exact: '/control',
    }),
    suggestions: [],
  },
  contactsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/contacts',
    }),
    suggestions: ['Add a new contact for me'],
  },
  transactionsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/transactions',
    }),
    suggestions: [],
  },
  calendarPage: {
    pathnameMatcher: createPathnameMatcher({
      exact: '/calendar',
    }),
    suggestions: [],
  },
  eventsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/events',
      lastSegment: {
        isNot: 'calendar',
      },
    }),
    suggestions: ['How do I create an event?', 'How do I manage activities?', 'What types of activities can I create?'],
  },
  pricingSchemesPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/pricing_schemes',
    }),
    suggestions: ['What are pricing schemes used for?'],
  },
  paymentOptionsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/payment_options',
    }),
    suggestions: ['What payment options are available?'],
  },
  proposalTemplatesPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/proposal_templates',
    }),
    suggestions: ['How do I customize proposal templates?', 'What can I include in a proposal template?'],
  },
  utilityTariffsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/utility_tariffs',
    }),
    suggestions: ['What information do I need for a utility tariff?'],
  },
  rolesPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/roles',
      lastSegment: {
        isNot: ['permissions_role', 'myroles'],
      },
    }),
    suggestions: ['How do I create a role?', 'How do I assign permissions to roles?', 'What are roles used for?'],
  },
  componentManagementPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/component_',
    }),
    suggestions: ['How do I activate components?'],
  },
  incentivesPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/incentives',
    }),
    suggestions: ['How do I configure incentives?'],
  },
  addersPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/adders',
    }),
    suggestions: [
      'What are adders used for?',
      'How do I configure adder pricing?',
      'What types of adders can I create?',
    ],
  },
  batterySchemesPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/battery_schemes',
    }),
    suggestions: [
      'What are battery schemes?',
      'How do battery control schemes work?',
      'How do I set up battery charging and discharging rules?',
    ],
  },
  costingsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/costings',
    }),
    suggestions: [
      'How do I configure costings?',
      'What are costing schemes used for?',
      'How do I set up COGS in costings?',
    ],
  },
  commissionsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/commissions',
    }),
    suggestions: ['How do I configure commissions?', 'How do commissions work?'],
  },
  contractsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/contracts',
    }),
    suggestions: ['What can I include in a contract template?', 'How do I customize contract templates?'],
  },
  testimonialsPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/testimonials',
    }),
    suggestions: ['What are testimonials used for?', 'How do testimonials appear in proposals?'],
  },
  customerCheckoutPage: {
    pathnameMatcher: createPathnameMatcher({
      startsWith: '/customer_checkout',
    }),
    suggestions: [
      'How do I configure the checkout experience?',
      'What checkout options are available?',
      'What information is shown during checkout?',
    ],
  },
  // Fallback category - always matches when no other contextual matches are found
  // Curently we are deciding to show nothi
  default: {
    suggestions: [],
  },
}

export const getMatchingSuggestions = (
  pathname: string,
  isStudioLoaded: boolean,
  hasSelectedSystem: boolean,
  selectedSystem?: any
): string[] => {
  const context: SuggestionContext = {
    pathname,
    isStudioLoaded,
    hasSelectedSystem,
    selectedSystem,
  }

  const allSuggestionsWithPriority: Array<{ text: string; priority: number }> = []
  let hasContextualMatch = false

  // Process all configs except default
  Object.entries(MESSAGE_SUGGESTIONS).forEach(([key, config]) => {
    if (key === 'default') {
      return
    }

    // Check if config matches basic requirements
    if (!doesConfigMatch(context, config)) {
      return
    }

    // Check custom validator if provided
    if (config.customValidator && !config.customValidator(context)) {
      return
    }

    hasContextualMatch = true
    const configPriority = config.priority || 0

    // Process each suggestion in the config
    config.suggestions.forEach((suggestion) => {
      let suggestionText: string
      let suggestionPriority = configPriority
      let shouldInclude = true

      // Handle both string and SuggestionItem formats
      if (typeof suggestion === 'string') {
        suggestionText = suggestion
      } else {
        suggestionText = suggestion.text
        suggestionPriority = suggestion.priority ?? configPriority

        if (suggestion.actionType) {
          const validation = validateActionRequirementsForSuggestion(suggestion.actionType, pathname, selectedSystem)
          shouldInclude = validation.isValid
        }

        if (shouldInclude && suggestion.customValidator) {
          shouldInclude = suggestion.customValidator(context)
        }
      }

      if (shouldInclude) {
        allSuggestionsWithPriority.push({ text: suggestionText, priority: suggestionPriority })
      }
    })
  })

  const textToPriority = new Map<string, number>()
  allSuggestionsWithPriority.forEach(({ text, priority }) => {
    const currentPriority = textToPriority.get(text)
    if (currentPriority === undefined || priority > currentPriority) {
      textToPriority.set(text, priority)
    }
  })

  const uniqueSuggestions = Array.from(textToPriority.entries()).map(([text, priority]) => ({
    text,
    priority,
  }))

  const suggestionsByPriority = new Map<number, Array<{ text: string; priority: number }>>()
  uniqueSuggestions.forEach((suggestion) => {
    const priority = suggestion.priority
    if (!suggestionsByPriority.has(priority)) {
      suggestionsByPriority.set(priority, [])
    }
    suggestionsByPriority.get(priority)!.push(suggestion)
  })

  const shuffledByPriority: Array<{ text: string; priority: number }> = []
  const sortedPriorities = Array.from(suggestionsByPriority.keys()).sort((a, b) => b - a)

  sortedPriorities.forEach((priority) => {
    const group = suggestionsByPriority.get(priority)!
    for (let i = group.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[group[i], group[j]] = [group[j], group[i]]
    }
    shuffledByPriority.push(...group)
  })

  return shuffledByPriority.slice(0, MAX_SUGGESTIONS).map((s) => s.text)
}

function doesConfigMatch(context: SuggestionContext, config: MessageSuggestionConfig): boolean {
  let matches = true

  if (config.pathnameMatcher) {
    matches = matches && config.pathnameMatcher(context.pathname)
  }

  if (config.requiresDesignTab) {
    const designTabPattern = /^\/projects\/(\d+)\/design/
    matches = matches && designTabPattern.test(context.pathname)
  }

  if (config.requiresStudioLoaded) {
    matches = matches && context.isStudioLoaded
  }

  if (config.requiresSelectedSystem) {
    matches = matches && context.hasSelectedSystem
  }

  if (config.requiresPanels) {
    if (!context.hasSelectedSystem || !context.selectedSystem) {
      matches = false
    } else {
      const panelCount = context.selectedSystem.moduleQuantity()
      matches = matches && panelCount > 0
    }
  }

  if (config.requiresBattery) {
    if (!context.hasSelectedSystem || !context.selectedSystem) {
      matches = false
    } else {
      const batteryCount = context.selectedSystem.batteries().length
      matches = matches && batteryCount > 0
    }
  }

  return matches
}

function validateActionRequirementsForSuggestion(
  actionType: string,
  pathname: string,
  selectedSystem: any
): { isValid: boolean } {
  const validation = validateActionRequirements(actionType, pathname, selectedSystem)
  return { isValid: validation.isValid }
}
